import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Play, CheckCircle2, Circle, Award, BookOpen, ChevronDown, ChevronUp, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoursePlayer() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quizFeedback, setQuizFeedback] = useState(null);

  useEffect(() => {
    axios.get(`${API}/course-content/${slug}`, { withCredentials: true })
      .then(r => { if (r.data && !r.data.detail) setData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const submitQuiz = async (moduleIndex) => {
    const answers = quizAnswers[moduleIndex] || [];
    setSubmitting(true);
    try {
      const { data: result } = await axios.post(`${API}/course-content/quiz`, {
        course_slug: slug,
        module_index: moduleIndex,
        answers
      }, { withCredentials: true });
      setQuizFeedback({ moduleIndex, ...result });
      // Refresh content
      const refreshed = await axios.get(`${API}/course-content/${slug}`, { withCredentials: true });
      setData(refreshed.data);
    } catch {}
    setSubmitting(false);
  };

  const isModuleComplete = (idx) => data?.progress?.completed_modules?.includes(idx) || false;
  const getQuizResult = (idx) => data?.quiz_results?.find(r => r.module_index === idx);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading course content...</div>;
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Access denied or course not found. <Link to="/dashboard" className="text-[#0077B6] ml-2">Back to Dashboard</Link></div>;

  const { course, quizzes } = data;
  const progressPct = data.progress?.progress_pct || 0;

  return (
    <div data-testid="course-player" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <Link to="/dashboard" className="flex items-center gap-2 text-base text-zinc-300 hover:text-[#0077B6] mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} /> Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Course Player</p>
            <h1 className="text-3xl font-bold tracking-tight text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{course.title}</h1>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{progressPct}%</p>
            <p className="text-sm text-zinc-300">Complete</p>
            <div className="w-32 h-2 bg-[#262626] mt-1"><div className="h-2 bg-[#0077B6] transition-all" style={{ width: `${progressPct}%` }} /></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Module Sidebar */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Modules</h2>
            <div className="border border-[#262626]">
              {(course.modules || []).map((mod, i) => {
                const done = isModuleComplete(i);
                const result = getQuizResult(i);
                return (
                  <button
                    key={i}
                    onClick={() => setActiveModule(i)}
                    data-testid={`module-nav-${i}`}
                    className={`w-full text-left p-4 border-b border-[#262626] last:border-b-0 transition-colors flex items-start gap-3 ${
                      activeModule === i ? 'bg-[#0077B6]/10 border-l-2 border-l-[#0077B6]' : 'hover:bg-[#1a1a1a]'
                    }`}
                  >
                    {done
                      ? <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                      : <Circle className="w-5 h-5 text-zinc-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    }
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${activeModule === i ? 'text-[#0077B6]' : 'text-white'}`}>Week {i + 1}</p>
                      <p className="text-xs text-zinc-400 truncate">{mod}</p>
                      {result && (
                        <span className={`text-xs font-mono mt-1 inline-block px-1.5 py-0.5 ${result.passed ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Quiz: {result.score}%
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-2">
            {course.modules && course.modules[activeModule] && (
              <div>
                {/* Module Header */}
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xl font-mono font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Week {activeModule + 1}</span>
                    {isModuleComplete(activeModule) && <CheckCircle2 className="w-6 h-6 text-green-400" strokeWidth={2} />}
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {course.modules[activeModule]}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-zinc-300">
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#0077B6]" strokeWidth={1.5} /> 2 hours</span>
                    <span className="flex items-center gap-1"><BookOpen className="w-4 h-4 text-[#0077B6]" strokeWidth={1.5} /> Lecture + Practice</span>
                  </div>
                </div>

                {/* Lecture Video */}
                <div className="border border-[#262626] bg-[#111111] mb-6">
                  {(() => {
                    const videoInfo = (course.video_urls || []).find(v => v.module_index === activeModule);
                    const folderUrl = course.video_folder_url;
                    if (videoInfo && videoInfo.url) {
                      // Direct embed URL provided by admin
                      return (
                        <>
                          <div className="aspect-video bg-[#0A0A0A]">
                            <iframe
                              src={videoInfo.url}
                              className="w-full h-full"
                              title={videoInfo.title || `Week ${activeModule + 1} Lecture`}
                              allowFullScreen
                              allow="autoplay"
                              frameBorder="0"
                            />
                          </div>
                          <div className="p-4 border-t border-[#262626]">
                            <p className="text-sm text-white font-medium">{videoInfo.title}</p>
                            <p className="text-xs text-zinc-400 mt-1">
                              <strong className="text-white">Skill Focus:</strong> {course.skills && course.skills[activeModule] ? course.skills[activeModule] : 'Module skill'}
                            </p>
                          </div>
                        </>
                      );
                    } else if (videoInfo && folderUrl) {
                      // Video exists in OneDrive folder — link to it
                      return (
                        <>
                          <div className="aspect-video bg-[#0A0A0A] flex items-center justify-center border-b border-[#262626]">
                            <div className="text-center max-w-md px-6">
                              <Play className="w-16 h-16 text-[#0077B6] mx-auto mb-3" strokeWidth={1.5} />
                              <p className="text-lg text-white font-semibold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{videoInfo.title}</p>
                              <p className="text-sm text-zinc-400 mb-4">{videoInfo.filename}</p>
                              <a
                                href={folderUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="watch-video-btn"
                                className="brand-btn text-sm px-6 py-3 inline-flex items-center gap-2"
                              >
                                <Play className="w-4 h-4" strokeWidth={1.5} /> Open Lecture in OneDrive
                              </a>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-base text-zinc-300">
                              <strong className="text-white">Skill Focus:</strong> {course.skills && course.skills[activeModule] ? course.skills[activeModule] : 'Module skill'}
                            </p>
                          </div>
                        </>
                      );
                    } else {
                      // No video yet — placeholder
                      return (
                        <>
                          <div className="aspect-video bg-[#0A0A0A] flex items-center justify-center border-b border-[#262626]">
                            <div className="text-center">
                              <Play className="w-16 h-16 text-[#0077B6] mx-auto mb-3" strokeWidth={1.5} />
                              <p className="text-lg text-white font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Lecture: {course.modules[activeModule]}</p>
                              <p className="text-sm text-zinc-400 mt-1">Video content will be available here</p>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-base text-zinc-300">
                              <strong className="text-white">Skill Focus:</strong> {course.skills && course.skills[activeModule] ? course.skills[activeModule] : 'Module skill'}
                            </p>
                          </div>
                        </>
                      );
                    }
                  })()}
                </div>

                {/* Quiz Section */}
                {quizzes && quizzes[activeModule] && (
                  <div className="border border-[#262626] bg-[#111111] p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <Award className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
                      <div>
                        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Module Quiz</p>
                        <p className="text-sm text-zinc-300">Score 50% or higher to mark this module complete</p>
                      </div>
                    </div>

                    {quizzes[activeModule].questions.map((q, qi) => (
                      <div key={qi} data-testid={`quiz-q-${qi}`} className="mb-6 p-4 bg-[#0A0A0A] border border-[#262626]">
                        <p className="text-base text-white font-medium mb-3">{qi + 1}. {q.q}</p>
                        <div className="space-y-2">
                          {q.options.map((opt, oi) => {
                            const selected = (quizAnswers[activeModule] || [])[qi] === oi;
                            return (
                              <button
                                key={oi}
                                onClick={() => {
                                  const prev = quizAnswers[activeModule] || [];
                                  const newAnswers = [...prev];
                                  newAnswers[qi] = oi;
                                  setQuizAnswers({ ...quizAnswers, [activeModule]: newAnswers });
                                }}
                                className={`w-full text-left p-3 border transition-colors flex items-center gap-3 ${
                                  selected ? 'border-[#0077B6] bg-[#0077B6]/10 text-white' : 'border-[#262626] text-zinc-300 hover:bg-[#1a1a1a]'
                                }`}
                              >
                                <span className={`w-5 h-5 border-2 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#0077B6]' : 'border-zinc-500'}`}>
                                  {selected && <span className="w-2.5 h-2.5 bg-[#0077B6] rounded-full" />}
                                </span>
                                <span className="text-base">{opt}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* Quiz Feedback */}
                    {quizFeedback && quizFeedback.moduleIndex === activeModule && (
                      <div data-testid="quiz-feedback" className={`p-4 mb-4 border ${quizFeedback.passed ? 'border-green-600 bg-green-900/20' : 'border-red-600 bg-red-900/20'}`}>
                        <p className={`text-lg font-bold ${quizFeedback.passed ? 'text-green-400' : 'text-red-400'}`}>
                          {quizFeedback.passed ? 'Passed!' : 'Not Passed'} — Score: {quizFeedback.score}% ({quizFeedback.correct}/{quizFeedback.total})
                        </p>
                        <p className="text-sm text-zinc-300 mt-1">
                          {quizFeedback.passed ? 'Module marked as complete. Great work!' : 'You need 50% to pass. Review the material and try again.'}
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => submitQuiz(activeModule)}
                      disabled={submitting || !(quizAnswers[activeModule]?.length)}
                      data-testid="submit-quiz"
                      className="brand-btn text-base px-8 py-3 disabled:opacity-40 flex items-center gap-2"
                    >
                      {submitting ? 'Submitting...' : 'Submit Quiz'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
